// @ts-nocheck
import axios from 'axios';
import { BaseTransformer } from '../base-transformer';
import type { IAdapter, InitArguments } from '../event-client';
import type { EventPayloads, EventTypes, LoginPayload } from '../events';
const subscribedEvents: Array<EventTypes> = [
	'view-cart',
	'add-to-cart',
	'product-search',
	'product-view',
	'page-view',
	'remove-from-cart',
	'proceed-to-checkout',
	'checkout-page-landing',
	'checkout-payment-method-select',
	'checkout-thankyou',
	'purchase',
];

export const generateUniqueEventId = (eventName: string) => {
	const now = new Date();
	const timestamp = now.toISOString();
	return `${eventName}_${timestamp}`;
};

class MetaPixelTransformer {
	static transformPurchaseEvent(data: EventPayloads['purchase']): {
		name: string;
		data: Record<string, unknown>;
	} {
		// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
		return {
			name: 'Purchase',
			data: {
				content_ids: data.content_ids,
				content_type: 'product',
				contents: data.contents,
				currency: data.currency,
				value: data.value,
				num_items: data.num_items,
			},
		};
	}

	static transformViewCart(data: EventPayloads['view-cart']): {
		name: string;
		data: Record<string, unknown>;
	} {
		// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
		return {
			name: 'view-cart',
			data: {
				source: data.source,
				itemCount: data.itemCount,
				subTotal: data.subTotalinPkr,
				currencyCode: data.currencyCode,
			},
		};
	}

	static transformAddToCartEvent(data: EventPayloads['add-to-cart']): {
		name: string;
		data: Record<string, unknown>;
	} {
		return {
			name: 'AddToCart',
			data: {
				content_ids: data.content_ids,
				content_type: 'product',
				contents: data.contents,
				currency: data.currency,
				value: data.value,
			},
		};
	}

	static transformInitiateCheckoutEvent(
		data: EventPayloads['proceed-to-checkout'],
	): {
		name: string;
		data: Record<string, unknown>;
	} {
		return {
			name: 'InitiateCheckout',
			data: {
				content_ids: data.content_ids,
				contents: data.contents,
				currency: data.currency,
				value: data.subTotalinPkr,
				num_items: data.num_items,
			},
		};
	}

	static transformPageViewEvent(data: EventPayloads['page-view']): {
		name: string;
		data: Record<string, unknown>;
	} {
		return {
			name: 'PageView',
			data: data,
		};
	}

	static transformAddPaymentInfoEvent(
		data: EventPayloads['checkout-payment-method-select'],
	): { name: string; data: Record<string, unknown> } {
		return {
			name: 'AddPaymentInfo',
			data: {
				value: data.value,
				content_ids: data.content_ids,
				content_name: data.content_name,
				content_type: 'product',
				currency: data.currency,
				status: data.status,
				num_items: data.num_items,
			},
		};
	}

	static transformCheckoutPageLanding(
		data: EventPayloads['checkout-page-landing'],
	): { name: string; data: Record<string, unknown> } {
		return {
			name: 'checkout-page-landing',
			data: {
				checkoutMount: data.checkoutMount,
				checkoutHash: data.checkoutHash,
				itemCount: data.itemCount,
				subTotal: data.subTotalInPkr,
				currencyCode: data.currencyCode,
				checkoutRedesign: data?.checkoutRedesign,
			},
		};
	}

	static transformSearchEvent(data: EventPayloads['product-search']): {
		name: string;
		data: Record<string, unknown>;
	} {
		return { name: 'Search', data: { search_string: data.query } };
	}

	static transformViewContentEvent(data: EventPayloads['product-view']): {
		name: string;
		data: Record<string, unknown>;
	} {
		return {
			name: 'ViewContent',
			data: {
				content_ids: [data.id],
				content_type: 'product_group',
				currency: data.currency,
				value: data.price,
			},
		};
	}
}

const TransformerMap = {
	'add-to-cart': MetaPixelTransformer.transformAddToCartEvent,
	'checkout-payment-method-select':
		MetaPixelTransformer.transformAddPaymentInfoEvent,
	'product-search': MetaPixelTransformer.transformSearchEvent,
	'product-view': MetaPixelTransformer.transformViewContentEvent,
	'page-view': MetaPixelTransformer.transformPageViewEvent,
	'remove-from-cart': BaseTransformer.transformRemoveFromCartEvent,
	'proceed-to-checkout': MetaPixelTransformer.transformInitiateCheckoutEvent,
	'checkout-page-landing': MetaPixelTransformer.transformCheckoutPageLanding,
	'view-cart': MetaPixelTransformer.transformViewCart,
	purchase: MetaPixelTransformer.transformPurchaseEvent,
};

export class MetaPixelAdapter implements IAdapter {
	name: string = 'metapixel';
	queue: Array<{ name: EventTypes; data: EventPayloads[EventTypes] }> = [];
	isInitialized = false;
	apiBaseUrl?: string;
	storeDomain?: string;
	metaPixelId?: string;
	metaAccessToken?: string;
	countryCode?: string;

	private getCookie(name: string): string | null {
		const match = document.cookie.match(
			new RegExp(
				'(?:^|; )' +
					name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') +
					'=([^;]*)',
			),
		);
		return match ? decodeURIComponent(match[1]) : null;
	}

	async sendFbConversion(payload: {
		event_name: string;
		content?: object;
		custom_data?: object;
		eventId?: string;
	}): Promise<void> {
		if (!this.apiBaseUrl) return;
		const eventId =
			payload.eventId ?? generateUniqueEventId(payload.event_name);
		const userRaw = localStorage.getItem('logan');
		const user = userRaw ? JSON.parse(userRaw) : null;
		const data = {
			event_data: {
				event_name: payload.event_name,
				event_id: eventId,
				action_source_url: window.location.href,
			},
			customer_data: {
				first_name: user?.firstName ?? '',
				last_name: user?.lastName ?? '',
				email: user?.email ?? '',
				country: this.countryCode ?? null,
				zipCode: this.getCookie('zip_code'),
				fbp: this.getCookie('_fbp'),
				fbc: this.getCookie('_fbc')
					? this.getCookie('_fbc').replace(/__DOT__/g, '.')
					: null,
			},
			...payload,
			eventId,
		} as any;

		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
			'Access-Control-Allow-Origin': '*',
		};
		if (this.storeDomain) headers['X-Store'] = this.storeDomain;
		if (this.metaPixelId) headers['Meta-Pixel-Id'] = this.metaPixelId;
		if (this.metaAccessToken)
			headers['Meta-Access-Token'] = this.metaAccessToken;

		try {
			await axios.post(
				`${this.apiBaseUrl}/server_events/fb_conversions`,
				JSON.stringify(data),
				{ headers },
			);
		} catch {
			// no-op
		}
	}

	async init(arg: InitArguments['metapixel']): Promise<void> {
		this.apiBaseUrl = arg.apiBaseUrl;
		this.storeDomain = arg.storeDomain;
		this.metaPixelId = arg.metaPixelId;
		this.metaAccessToken = arg.metaAccessToken;
		this.countryCode = arg.countryCode;
		// @ts-ignore
		(function (
			f: Window,
			b: Document,
			e: string,
			v: string,
			n: any,
			t: any,
			s: any,
		) {
			if (f.fbq) return;
			n = f.fbq = function (...args: any[]) {
				n!.callMethod ? n!.callMethod.apply(n, args) : n!.queue.push(args);
			} as any;
			// @ts-ignore
			if (!f._fbq) f._fbq = n;
			n!.push = n;
			n!.loaded = !0;
			n!.version = '2.0';
			n!.queue = [];
			t = b.createElement(e)!;
			t!.async = !0;
			t!.src = v;

			// Check if there are any script tags in the document
			const existingScripts = b.getElementsByTagName(e);
			if (existingScripts.length > 0) {
				s = existingScripts[0];
				s.parentNode?.insertBefore(t, s);
			} else {
				// If no script tags exist, append to head or body
				(b.head || b.body).appendChild(t);
			}
		})(
			window,
			document,
			'script',
			'https://connect.facebook.net/en_US/fbevents.js',
		);
		// @ts-ignore

		if (window.fbq) {
			const pixelId = arg.pixelId[0];
			if (!window._fbq || !window._fbq[pixelId]) {
				window.fbq('init', pixelId);
				window._fbq[pixelId] = true;
			} else if (arg.pixelId.length > 1 && !window._fbq[arg.pixelId[1]]) {
				window.fbq('init', arg.pixelId[1]);
				window._fbq[arg.pixelId[1]] = true;
			}
		}

		this.isInitialized = true;
	}

	sendEvent<T extends EventTypes>(name: T, data: EventPayloads[T]): void {
		if (!subscribedEvents.includes(name)) {
			return;
		}
		if (!this.isInitialized) {
			this.queue.push({ name, data });
			return;
		}

		this.queue.push({ name, data });

		this.queue.forEach((event) => {
			// @ts-ignore
			const { data, name } = TransformerMap[event.name]
				? TransformerMap[event.name](event.data)
				: { data: event.data, name: event.name };
			if (name || event.name) {
				const eventName = name ?? event.name;
				const eventId = generateUniqueEventId(eventName);
				window.fbq('track', eventName, data, { eventID: eventId });
				this.sendFbConversion({
					event_name: eventName,
					eventId: eventId,
					content: data,
				});
			}
		});
		this.queue.splice(0, this.queue.length);
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	login(_: LoginPayload) {
		// no-op
	}
	logout(): void {
		// no-op
	}
}
